import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { breachcrumbPath } from '../utils/breadcrumbConst';
import { createToastMessage } from '../utils/createToastMessage';
import { studyResultschema } from '../utils/validations';
import { useApi } from './useApi';
import { showAlertModal } from '../pages/BulkEmail/TemplateForms/Shared/alertModal';

export const useStudyResult = () => {
  const navigate = useNavigate();
  const { getOne, getIndex, getManyById, updateData, deleteMany } = useApi();

  const baseUrl = 'core/studyResult';
  const controllerName = 'results';
  const name = 'study results';
  const nameSingle = 'study result';

  const [studyResults, setStudyResults] = useState<IIndexResult[] | []>([]);
  const [filterStudyResults, setFilterStudyResults] = useState<IIndexResult[] | []>([]);

  const [studyResult, setStudyResult] = useState<IStudyResult>();
  const [saveName, setSavedName] = useState('');
  const [status, setStatus] = useState('');

  const [projects, setProjects] = useState<{ value: number; label: string }[]>([]);

  const [selectStudyResults, setSelectStudyResults] = useState<number[]>([]);
  const [changedFields, setChangedField] = useState<string[]>([]);

  const [pageType, setPageType] = useState('add');
  const [toastMessage, setToastMessage] = useState<IToastMessage>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [selectedValue, setSelectedValue] = useState('');

  const [resultStatusFilter, setResultStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const [resourceFile, setResourceFile] = useState<File>();
  const [resourceFileDelect, setResourceFileDelect] = useState(false);

  const [history, setHistory] = useState<IHistoryLog[]>([]);

  const getBreadcrumbs = () => {
    const crumbs = [breachcrumbPath.core, breachcrumbPath.studyResult];
    if (projects.length === 1) {
      crumbs.push({
        url: `${breachcrumbPath.studyResult.url}/${studyResults[0].id}/change`,
        name: `${studyResults[0].id}` || '',
      });
      crumbs.push({ url: '', name: 'Delete' });
    } else {
      crumbs.push({ url: '', name: 'Delete multiple objects' });
    }
    return crumbs;
  };

  const handleChange = (value: string | number | boolean, field: string) => {
    setStudyResult((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (!changedFields.includes(field)) {
      setChangedField((oldArray) => [...oldArray, field]);
    }
  };

  const handleCheck = (id: number) => {
    if (selectStudyResults.includes(id)) {
      setSelectStudyResults((oldArray) => oldArray.filter((arr) => id !== arr));
    } else {
      setSelectStudyResults((oldArray) => [...oldArray, id]);
    }
  };

  const handleMenuAction = (action: string) => {
    if (action === 'delete_selected') {
      navigate('delete', { state: { ids: selectStudyResults } });
    }
  };

  // API Calls
  const getStudyResults = async (isIndex = false) => {
    try {
      const studyResultInfo = await getIndex(`${controllerName}/study`);
      setStudyResults(studyResultInfo);
      if (isIndex) {
        setFilterStudyResults(studyResultInfo);
      }
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const getStudyResult = async (id: number) => {
    try {
      const studyResultInfo = await getOne(id, `admin/${controllerName}`, '/study');
      studyResultInfo.result.type = 'study';
      setStudyResult(studyResultInfo.result);
      setSavedName(studyResultInfo.result.id);
      setSelectStudyResults(studyResultInfo.result.id);
      setProjects(studyResultInfo.projects);
      setStatus(studyResultInfo.result.status);
    } catch (error) {
      throw new Error('error');
    }
  };

  const getStudyResultsById = async (ids: number[]) => {
    try {
      const studyResultInfo = await getManyById(ids, `admin/${controllerName}`);
      setStudyResults(studyResultInfo);
    } catch (error) {
      throw new Error('error');
    }
  };

  const deleteStudyResults = async (id: number[]) => {
    try {
      const results = await deleteMany(id, `admin/${controllerName}`, { type: 'study' });
      return results;
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const getHistoryLog = async (id: string) => {
    try {
      const resResult = await getIndex(`admin/${controllerName}/history/${id}/study`);
      setHistory(resResult.historyLogs);
      setStudyResult(resResult.resultInfo);
    } catch (error: any) {
      setErrors({ message: error.message });
    }
  };

  const updateStudyResult = async (values: any, id: number) => {
    try {
      const updatedStudyResult = await updateData(values, `admin/${controllerName}/${id}`);
      return updatedStudyResult;
    } catch (error) {
      throw new Error('error');
    }
  };

  const handleUpdate = async (type: string) => {
    try {
      if (!studyResult?.id) throw new Error(`Failed to update ${nameSingle}`);
      setErrors({});

      const resultsInfo = { ...studyResult };
      if (resultsInfo.resource_website_url === 'null') {
        delete resultsInfo.resource_website_url;
      }
      if (resultsInfo.share_on_hmc && (!resultsInfo.hmc_identifier || !resultsInfo.hmc_page_url)) {
        await showAlertModal('You must fill the HMC identier and URL!');
        return null;
      }
      const validedValues: any = await studyResultschema.validate(
        {
          ...resultsInfo,
        },
        { abortEarly: false, stripUnknown: true }
      );

      validedValues.updateStatus = status !== validedValues.status;

      if (resourceFileDelect) {
        validedValues.deleteFile = resourceFileDelect;
        validedValues.deletedFileName = studyResult.resource_file;
      }

      const data = new FormData();
      Object.keys(validedValues).forEach((key) => {
        data.append(key, validedValues[key]);
      });

      if (resourceFile) {
        data.append('resourceFile', resourceFile);
      }

      data.append(
        'changedFields',
        changedFields.filter((change: string) => change !== 'status').join(', ')
      );

      const studyResultInfo = await updateStudyResult(data, studyResult?.id);
      if (studyResultInfo.error) throw new Error(studyResultInfo.error);

      studyResultInfo.type = 'study';
      setStudyResult({
        ...studyResult,
        ...studyResultInfo,
      });
      setStatus(studyResultInfo.status);
      setResourceFileDelect(false);
      setResourceFile(undefined);

      let message;
      switch (type) {
        case 'edit':
          message = createToastMessage(
            'edit',
            nameSingle,
            `StudyResult object (${studyResultInfo.id})`,
            `/${baseUrl}/${studyResultInfo.id}/change`
          );
          break;
        case 'save':
          message = createToastMessage(
            'save',
            nameSingle,
            `StudyResult object (${studyResultInfo.id})`,
            `/${baseUrl}/${studyResultInfo.id}/change`
          );
          break;
        default:
          break;
      }
      if (message) {
        setToastMessage(message);
      }
      if (type === 'save') {
        setStudyResult({});
        navigate(`/${baseUrl}`, { state: { toastMessage: message } });
      }
    } catch (error: any) {
      if (error.inner) {
        const errorMessages: { [key: string]: string } = {};
        error.inner.forEach((element: any) => {
          errorMessages[element.path] = element.message;
        });
        setErrors(errorMessages);
      } else {
        setErrors({ fetch: error.message });
      }
    }
  };

  const handleDelete = async (ids: number[]) => {
    try {
      const deleteCount = await deleteStudyResults(ids);
      const message =
        deleteCount > 1
          ? createToastMessage('deletes', name, deleteCount)
          : createToastMessage('delete', nameSingle, `${studyResults[0]?.id}` || '');
      navigate(`/${baseUrl}`, { state: { toastMessage: message } });
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const handleSubmitButton = (type: string) => {
    handleUpdate(type);
  };

  const handleUpdateFilter = (value: string | number, key: string) => {
    if (typeof value === 'number') return;
    switch (key) {
      case 'result_status':
        setResultStatusFilter(value);
        break;
      case 'search':
        setSearchFilter(value);
        break;
      default:
        break;
    }
  };

  const handleSearch = () => {
    if (!studyResults) return [];
    const searchValue = studyResults.filter((res) => {
      let result = false;
      const id = `${res.id}`;
      const projectId = `${res.project_id}`;
      if (id?.includes(searchFilter)) {
        result = true;
      }
      if (projectId?.includes(searchFilter)) {
        result = true;
      }
      if (res.project_name?.toLowerCase().includes(searchFilter.toLowerCase())) {
        result = true;
      }
      if (resultStatusFilter !== '' && resultStatusFilter !== res.result_status) return false;

      if (res.project_name?.toLowerCase().includes(searchFilter.toLowerCase())) {
        return true;
      }

      if (res.pi_user?.toLowerCase().includes(searchFilter.toLowerCase())) {
        return true;
      }

      if (res.updated_by_user?.toLowerCase().includes(searchFilter.toLowerCase())) {
        return true;
      }

      if (res.team_members) {
        const filterTeamMembers = res.team_members.filter((member) =>
          member.toLowerCase().includes(searchFilter.toLowerCase())
        );
        if (filterTeamMembers.length > 0) return true;
      }
      return result;
    });
    setFilterStudyResults(searchValue);
    return searchValue;
  };

  return {
    nameSingle,
    name,
    baseUrl,
    saveName,
    studyResult,
    filterStudyResults,
    studyResults,
    selectStudyResults,
    pageType,
    setPageType,
    toastMessage,
    getStudyResult,
    setSelectStudyResults,
    getStudyResults,
    handleSubmitButton,
    setStudyResult,
    selectedValue,
    setSelectedValue,
    handleMenuAction,
    getStudyResultsById,
    handleCheck,
    handleChange,
    handleUpdate,
    handleSearch,
    handleDelete,
    errors,
    handleUpdateFilter,
    searchFilter,
    setSearchFilter,
    resultStatusFilter,
    resourceFile,
    setResourceFile,
    projects,
    getBreadcrumbs,
    history,
    getHistoryLog,
    resourceFileDelect,
    setResourceFileDelect,
  };
};
